'use client';

import { CheckIcon } from '@/components/ui/icons';
import { Package } from '@/core/models';
import { getClinicPartnerSubscriptionPackages } from '@/services/payment/payment.service';
import { Button, Flex, Tag, Text } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { FaCheckCircle } from 'react-icons/fa';

const PackageList = ({ onSubmit }: Props) => {
   const bottomRef = React.useRef<HTMLDivElement | null>(null);

   const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
   const [packageList, setPackageList] = React.useState<Package[]>([]);
   const fetchPackageList = useCallback(async () => {
      const [error, data] = await getClinicPartnerSubscriptionPackages();

      if (error) {
         console.log(error);
         setPackageList([]);
         return;
      }
      setPackageList(data.response);
   }, []);

   useEffect(() => {
      fetchPackageList();
   }, [fetchPackageList]);

   useEffect(() => {
      if (selectedPackage && bottomRef.current) {
         bottomRef.current.scrollIntoView({ behavior: 'smooth' });
      }
   }, [selectedPackage]);

   const handleSelectPackage = (packageId: string) => {
      setSelectedPackage(packageId);
   };

   return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
         {packageList.map(pack => (
            <div
               key={pack._id}
               style={{
                  background: '#1A1A1A',
                  borderRadius: '1rem',
                  width: '100%',
                  padding: '1rem',
                  color: 'white',
                  cursor: 'pointer',
                  position: 'relative',
                  outline: selectedPackage === pack._id ? '1px solid #F5E876' : 'none',
               }}
               onClick={() => handleSelectPackage(pack._id)}
            >
               {selectedPackage === pack._id && (
                  <Tag.Root
                     style={{
                        position: 'absolute',
                        top: '-0.8rem',
                        right: '1rem',
                        border: '1px solid #F5E876',
                        padding: '4px 8px',
                     }}
                  >
                     <Tag.StartElement>
                        <FaCheckCircle style={{ color: '#F5E876' }} />
                     </Tag.StartElement>
                     <Tag.Label>Selected</Tag.Label>
                  </Tag.Root>
               )}
               {/* Title */}
               <Flex
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ base: 'flex-start', md: 'end' }}
                  flexDirection={{ base: 'column', md: 'row' }}
                  paddingBottom="10px"
                  marginBottom="10px"
                  borderBottom="1px solid #FFFFFF1A"
                  gap="10px"
               >
                  <div>
                     <Text fontSize="xl" fontWeight="bold" mb="1" color="#F5E876">
                        {pack.productName}
                     </Text>
                     <Text fontSize="sm" color="white">
                        {pack.minimum_signup_on}-month minimum sign up
                     </Text>
                  </div>
                  <div>
                     <Text fontSize="2xl" fontWeight="bold" color="white">
                        £{pack.amount / 100}/mo
                     </Text>
                  </div>
               </Flex>
               <Flex
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ base: 'flex-start', md: 'end' }}
                  flexDirection={{ base: 'column', md: 'row' }}
                  paddingBottom="10px"
                  marginBottom="10px"
                  borderBottom="1px solid #FFFFFF1A"
                  gap="10px"
               >
                  <div>
                     <Text fontSize="sm" color="white">
                        {pack.patientsPerMonth} Patients/ month
                     </Text>
                  </div>
               </Flex>

               {/* Section: Profile Features */}
               {pack.mainFeature.map(feature => (
                  <div
                     key={feature._id}
                     style={{
                        marginBottom: '8px',
                        borderRadius: '0.5rem',
                        padding: '8px',
                        backgroundColor: '#FFFFFF1A',
                        border: '1px solid #FFFFFF1A',
                     }}
                  >
                     <Text fontWeight="500" mb="2" color="white" fontSize="sm">
                        {feature.mainFeature}
                     </Text>
                     <ul
                        style={{
                           color: '#FFFFFFCC',
                           fontSize: '12px',
                           display: 'flex',
                           flexDirection: 'column',
                           gap: 3,
                        }}
                     >
                        {feature.features.map(subFeature => (
                           <li key={subFeature._id} style={{ display: 'flex', gap: 10 }}>
                              <CheckIcon width={16} height={16} /> {subFeature.description}
                           </li>
                        ))}
                     </ul>
                  </div>
               ))}
            </div>
         ))}
         {selectedPackage && <Button onClick={() => onSubmit(selectedPackage)}>Continue</Button>}
         <div ref={bottomRef} />
      </div>
   );
};

export default PackageList;

export type Props = {
   onSubmit: (packageSelected: string) => void;
};
